import { ChangeDetectionStrategy, Component, Injector, OnInit } from "@angular/core";
import { Autobind } from "@common/lib/autobind.decorator/autobind.decorator";
import { ErrorHandlingUtilities } from "@common/lib/utilities/error-handling-utilities";
import { AdaptCommonDialogService } from "@common/ux/adapt-common-dialog/adapt-common-dialog.service";
import { IConfirmationDialogData } from "@common/ux/adapt-common-dialog/confirmation-dialog.component/confirmation-dialog.component";
import { BaseRoutedComponent } from "@common/ux/base-routed.component";
import { from, lastValueFrom } from "rxjs";
import { switchMap } from "rxjs/operators";
import { McpOAuthClientDialogComponent } from "../mcp-oauth-client-dialog/mcp-oauth-client-dialog.component";
import { IMcpOAuthClient, StakeholderServicesService } from "../stakeholder-services.service";

interface IMcpOAuthClientGridItem extends IMcpOAuthClient {
    Type: "CIMD domain" | "Registered client";
}

@Component({
    selector: "adapt-mcp-oauth-clients-page",
    templateUrl: "./mcp-oauth-clients-page.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class McpOAuthClientsPageComponent extends BaseRoutedComponent implements OnInit {
    public clients: IMcpOAuthClientGridItem[] = [];
    public error?: string;

    public constructor(
        private stakeholderServicesService: StakeholderServicesService,
        private commonDialogService: AdaptCommonDialogService,
        injector: Injector,
    ) {
        super(injector);
    }

    public ngOnInit() {
        this.notifyActivated();
        void this.loadClients();
    }

    @Autobind
    public async loadClients() {
        this.error = undefined;
        try {
            const clients = await this.stakeholderServicesService.getMcpOAuthClients();
            this.clients = clients.map((client) => ({
                ...client,
                Type: client.IsClientMetadataDomain ? "CIMD domain" : "Registered client",
            }));
        } catch (e) {
            this.error = ErrorHandlingUtilities.getHttpResponseMessage(e);
        }
    }

    @Autobind
    public addClient() {
        return this.openClientDialog();
    }

    @Autobind
    public editClient(client: IMcpOAuthClient) {
        return this.openClientDialog(client);
    }

    @Autobind
    public async approveClientMetadataDomain(client: IMcpOAuthClient) {
        const domain = client.ClientId;
        const dialogData: IConfirmationDialogData = {
            title: "Approve MCP OAuth client domain",
            message: `Approve ${domain}? All valid current and future OAuth client metadata documents from this exact domain will be allowed to authorise.`,
            confirmButtonText: "Approve domain",
        };

        if (!await lastValueFrom(this.commonDialogService.openConfirmationDialogWithBoolean(dialogData))) {
            return;
        }

        this.error = undefined;
        try {
            await this.stakeholderServicesService.approveMcpOAuthClientMetadataDomain(domain);
            await this.loadClients();
        } catch (e) {
            this.error = ErrorHandlingUtilities.getHttpResponseMessage(e);
        }
    }

    @Autobind
    public async deleteClient(client: IMcpOAuthClient) {
        const configurationWarning = client.IsConfigurationManaged
            ? " This client will be recreated from server configuration when the server next starts."
            : "";
        const message = client.IsClientMetadataDomain
            ? `Remove approval for ${client.ClientId} and revoke every metadata client and token from that domain?${configurationWarning}`
            : `Remove ${client.DisplayName} (${client.ClientId}) and revoke its existing authorisations and tokens?${configurationWarning}`;
        const dialogData: IConfirmationDialogData = {
            title: "Remove MCP OAuth client",
            message,
            confirmButtonText: "Remove",
        };

        if (!await lastValueFrom(this.commonDialogService.openConfirmationDialogWithBoolean(dialogData))) {
            return;
        }

        this.error = undefined;
        try {
            if (client.IsClientMetadataDomain) {
                await this.stakeholderServicesService.deleteMcpOAuthClientMetadataDomainApproval(client.ClientId);
            } else {
                await this.stakeholderServicesService.deleteMcpOAuthClient(client.ClientId);
            }
            await this.loadClients();
        } catch (e) {
            this.error = ErrorHandlingUtilities.getHttpResponseMessage(e);
        }
    }

    private openClientDialog(client?: IMcpOAuthClient) {
        this.error = undefined;
        return this.commonDialogService.open(McpOAuthClientDialogComponent, client).pipe(
            switchMap(() => from(this.loadClients())),
        );
    }
}
